/*
  File: service.ts
  Author: Binish Mookken
  Date: April-June 2024

  Description:
  This file contains functions for making API requests to the backend server.
  It includes functions for user authentication, retrieving user and agent data,
  fetching roles, creating agents, and setting and getting authentication tokens.
*/

import axios, { AxiosError } from "axios";
import qs from "qs";
import { User, Role } from "../types";

const service = axios.create({
  baseURL: process.env.REACT_APP_API_BASE_URL,
});

export const login = (username: string, password: string) => {
  const data = qs.stringify({ username, password });
  const headers = {
    "Content-Type": "application/x-www-form-urlencoded",
  };
  return service.post("/login", data, { headers });
};

export const setAuthToken = (token: string | null) => {
  if (token) {
    axios.defaults.headers.common["Authorization"] = `Bearer ${token}`;
  } else {
    delete axios.defaults.headers.common["Authorization"];
  }
};

const getAuthToken = () => {
  return localStorage.getItem("token");
};

export const getUsers = async () => {
  const token = getAuthToken();
  if (token) {
    service.defaults.headers.common["Authorization"] = `Bearer ${token}`;
  }

  try {
    const response = await service.get("/users");
    return response.data;
  } catch (error) {
    console.error("Error fetching users:", error);
    throw error;
  }
};

export const getAgents = async () => {
  const token = getAuthToken();
  if (token) {
    service.defaults.headers.common["Authorization"] = `Bearer ${token}`;
  }

  try {
    const response = await service.get("/agents");
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const getUser = async (id: string) => {
  const token = getAuthToken();
  if (token) {
    service.defaults.headers.common["Authorization"] = `Bearer ${token}`;
  }
  try {
    const response = await service.get(`/users/${id}`);
    return response.data;
  } catch (error: any) {
    throw error;
  }
};

export const getAgent = async (id: string) => {
  const token = getAuthToken();
  if (token) {
    service.defaults.headers.common["Authorization"] = `Bearer ${token}`;
  }

  try {
    const response = await service.get(`/agents/${id}`);
    return response.data;
  } catch (error) {
    console.error("Error fetching agent:", error);
    throw error;
  }
};

export const getAgentByEmail = async (email: string) => {
  const token = getAuthToken();
  try {
    const response = await service.get(`/agents/by-email/${email}`, {
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
    });
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const getRoles = async () => {
  const token = getAuthToken();
  if (token) {
    service.defaults.headers.common["Authorization"] = `Bearer ${token}`;
  }

  try {
    const response = await service.get("/roles");
    return response.data;
  } catch (error) {
    console.error("Error fetching roles:", error);
    throw error;
  }
};

export const createAgent = async (
  email: string,
  password: string,
  phone_number: string,
  first_name: string,
  last_name: string,
  enable_login: boolean,
  referred_by_id: string
) => {
  const data = {
    email,
    password,
    phone_number,
    first_name,
    last_name,
    enable_login,
    referred_by_id,
  };

  try {
    const response = await service.post("/agents", data);
    return response.data;
  } catch (error) {
    console.error("Error creating agent:", error);
    throw error;
  }
};

export const patchAgent = async (id: string, data: any) => {
  try {
    const response = await service.patch(`/agents/${id}`, data);
    return response.data;
  } catch (error) {
    console.error("Error updating agent:", error);
    throw error;
  }
};

export const createUser = async ({
  email,
  password,
  phoneNumber,
  firstName,
  lastName,
  roles,
}: {
  email: string;
  password: string;
  phoneNumber: string;
  firstName: string;
  lastName: string;
  roles: Role[];
}) => {
  try {
    const response = await service.post("/users", {
      email,
      password,
      phone_number: phoneNumber,
      first_name: firstName,
      last_name: lastName,
      roles,
    });

    return response.data;
  } catch (error) {
    console.error("Error creating user:", error);
    throw error;
  }
};

export const patchUser = async (id: string, data: any) => {
  try {
    const response = await service.patch(`/users/${id}`, data);
    return response.data;
  } catch (error) {
    console.error("Error updating agent:", error);
    throw error;
  }
};

export const changePassword = async (
  userId: string,
  currentPassword: string,
  newPassword: string
) => {
  try {
    const response = await service.post(`/users/${userId}/change-passwords`, {
      new_password: newPassword,
      current_password: currentPassword,
    });
  } catch (error) {
    console.error("Error changing password:", error);
    throw error;
  }
};

export const forgotPassword = async (email: string) => {
  try {
    const response = await service.post(`/users/${email}/forgot-password`, {});
    return response;
  } catch (error) {
    console.error("Error changing password:", error);
    throw error;
  }
};

export const resetPassword = async (
  email: string,
  newPassword: string,
  resetToken: string
) => {
  try {
    const response = await service.post(`/users/${email}/reset-password`, {
      new_password: newPassword,
      reset_token: resetToken,
    });
    return response;
  } catch (error) {
    console.error("Error changing password:", error);
    throw error;
  }
};

export const getPasscode = async (user_id: string) => {
  try {
    const response = await service.get(`/users/${user_id}/get-passcode`, {});
    return response;
  } catch (error) {
    console.error("Error changing password:", error);
    throw error;
  }
};

export const loginWithGoogle = async (
  credential: string,
  clientId: string,
  select_by: string = "btn"
) => {
  try {
    const response = await service.post("/auth/google", {
      credential,
      clientId,
      select_by,
    });
    return response.data; // Assuming the response contains user data including user_id
  } catch (error) {
    console.error("Error during Google login:", error);
    throw error;
  }
};

const hashCode = (str: string) => {
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    hash = str.charCodeAt(i) + ((hash << 5) - hash);
    hash = hash & hash; // Convert to 32bit integer
  }
  return Math.abs(hash);
};

export const getNameAbbrevation = (firstName: string, lastName: string) => {
  return `${firstName.charAt(0)}${lastName.charAt(0)}`;
};

export const getRandomAvatar = (seed: string) => {
  const styles = [
    "avataaars",
    "micah",
    "adventurer",
    "big-smile",
    "miniavs",
    "personas",
    "lorelei",
  ];

  // Get a consistent index based on the hash of the seed
  const index = hashCode(seed) % styles.length;
  const style = styles[index];

  return `https://api.dicebear.com/8.x/${style}/svg?seed=${seed}&skinColor=ffdbb4,fd9841,f8d25c,d08b5b,edb98a`;
};

export default {
  login,
  setAuthToken,
  getUsers,
  getRoles,
  createAgent,
  patchAgent,
  changePassword,
  getRandomAvatar,
  googleLogin: loginWithGoogle,
  getNameAbbrevation,
};
